/**
 * Demo Data Service
 * Provides mock data for demonstration purposes when API is unavailable
 */

import { CareDaily, MetricValue } from '../types';
import type { MetricConfig } from '../types';
import type { DashboardCategory } from './categoryService';
import type { WellnessDimension as ConfigWellnessDimension } from './dimensionService';
import type { ProgramSettings } from './programSettingsService';

/**
 * Generate realistic mock metric value
 */
function generateMockMetricValue(
  baseValue: number,
  variance: number = 10,
  updatedDaysAgo: number = 0
): MetricValue {
  const value = baseValue + (Math.random() - 0.5) * variance;
  const avg = baseValue;
  const std = variance / 3;
  const zscore = (value - avg) / std;
  
  return {
    value: Math.round(value * 100) / 100,
    std: Math.round(std * 100) / 100,
    avg: Math.round(avg * 100) / 100,
    zscore: Math.round(zscore * 100) / 100,
    display: value.toFixed(1),
    updated_ms: Date.now() - (updatedDaysAgo * 24 * 60 * 60 * 1000)
  };
}

/**
 * Generate mock time series data for trends
 */
function generateMockTimeSeries(
  baseValue: number,
  variance: number,
  days: number = 30
): Array<{ timestamp: number; value: number }> {
  const data = [];
  const now = Date.now();
  
  for (let i = days; i >= 0; i--) {
    const timestamp = now - (i * 24 * 60 * 60 * 1000);
    const trend = Math.sin(i / 7) * variance * 0.3; // Weekly pattern
    const noise = (Math.random() - 0.5) * variance;
    const value = baseValue + trend + noise;
    
    data.push({
      timestamp,
      value: Math.round(value * 100) / 100
    });
  }
  
  return data;
}

/**
 * Mock CareDaily Wellness Data
 */
export function getMockWellnessData(): CareDaily {
  const now = Date.now();
  
  return {
    trends_metadata: {
      name: "trends_metadata",
      value: {
        "wellnessScore": {
          category: "wellness",
          icon: "💚",
          title: "Care Daily Wellness Score",
          units: "%",
          window: 7,
          comment: "Overall wellness score based on multiple health indicators",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: true
        },
        "sleepScore": {
          category: "sleep",
          icon: "😴",
          title: "Sleep Quality Score",
          units: "%",
          window: 7,
          comment: "Quality of sleep based on duration and patterns",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: true
        },
        "activityScore": {
          category: "activity",
          icon: "🚶",
          title: "Activity Score",
          units: "%",
          window: 7,
          comment: "Physical activity and movement throughout the day",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: true
        },
        "bathroomVisits": {
          category: "bathroom",
          icon: "🚽",
          title: "Bathroom Visits",
          units: "visits",
          window: 1,
          comment: "Number of bathroom visits per day",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 20,
          daily: true
        },
        "nightWakeups": {
          category: "sleep",
          icon: "🌙",
          title: "Night Wake-ups",
          units: "times",
          window: 1,
          comment: "Number of times waking up during the night",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 10,
          daily: true
        },
        "timeInBed": {
          category: "sleep",
          icon: "🛏️",
          title: "Time in Bed",
          units: "hours",
          window: 1,
          comment: "Total time spent in bed",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 24,
          daily: true
        },
        "stepCount": {
          category: "activity",
          icon: "👣",
          title: "Step Count",
          units: "steps",
          window: 1,
          comment: "Total number of steps taken",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 10000,
          daily: true
        },
        "activeMinutes": {
          category: "activity",
          icon: "⏱️",
          title: "Active Minutes",
          units: "minutes",
          window: 1,
          comment: "Minutes of active movement",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 300,
          daily: true
        },
        "heartRate": {
          category: "health",
          icon: "❤️",
          title: "Resting Heart Rate",
          units: "bpm",
          window: 1,
          comment: "Average resting heart rate",
          operation: 0,
          updated_ms: now,
          min_value: 40,
          max_value: 120,
          daily: true
        },
        "socialInteractions": {
          category: "social",
          icon: "👥",
          title: "Social Interactions",
          units: "interactions",
          window: 1,
          comment: "Number of social interactions per day",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 20,
          daily: true
        },
        "moca": {
          category: "cognitive",
          icon: "🧠",
          title: "MoCA",
          units: "score",
          window: 90,
          comment: "Montreal Cognitive Assessment - Cognitive function screening",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 30,
          daily: false,
          thresholds: {
            healthy: { min: 26, max: 30, color: "green" },
            mild: { min: 18, max: 25, color: "yellow" },
            concern: { min: 0, max: 17, color: "red" }
          },
          target: 26
        },
        "twoMinuteStepRank": {
          category: "fitness",
          icon: "👟",
          title: "2 Minute Step Test Rank",
          units: "percentile",
          window: 30,
          comment: "Cardiovascular fitness ranking compared to peers",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: false,
          thresholds: {
            topQuartile: { min: 75, max: 100, color: "green" },
            middle: { min: 25, max: 74, color: "yellow" },
            bottomQuartile: { min: 0, max: 24, color: "red" }
          },
          target: 75
        },
        "chairStandRank": {
          category: "fitness",
          icon: "💺",
          title: "Chair Stand Rank",
          units: "percentile",
          window: 30,
          comment: "Lower body strength ranking compared to peers",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: false,
          thresholds: {
            topQuartile: { min: 75, max: 100, color: "green" },
            middle: { min: 25, max: 74, color: "yellow" },
            bottomQuartile: { min: 0, max: 24, color: "red" }
          },
          target: 75
        },
        "nutritionScore": {
          category: "nutrition",
          icon: "🥗",
          title: "Self Reported Nutrition",
          units: "%",
          window: 7,
          comment: "Percentage of daily nutritional goals met",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 100,
          daily: true,
          thresholds: {
            meeting: { min: 80, max: 100, color: "green" },
            partial: { min: 50, max: 79, color: "yellow" },
            poor: { min: 0, max: 49, color: "red" }
          },
          target: 80
        },
        "phq9": {
          category: "mental_health",
          icon: "💭",
          title: "PHQ-9",
          units: "score",
          window: 14,
          comment: "Patient Health Questionnaire - Depression screening",
          operation: 0,
          updated_ms: now,
          min_value: 0,
          max_value: 27,
          daily: false,
          thresholds: {
            minimal: { min: 0, max: 4, color: "green" },
            mild: { min: 5, max: 9, color: "yellow" },
            moderate: { min: 10, max: 27, color: "red" }
          },
          target: 4
        }
      }
    },
    trends: {
      name: "trends",
      stateDate: new Date().toISOString().split('T')[0],
      stateDateMs: now,
      value: {
        "wellnessScore": generateMockMetricValue(78, 10),
        "sleepScore": generateMockMetricValue(72, 15),
        "activityScore": generateMockMetricValue(65, 12),
        "bathroomVisits": generateMockMetricValue(6, 2),
        "nightWakeups": generateMockMetricValue(2, 1),
        "timeInBed": generateMockMetricValue(8.5, 1.5),
        "stepCount": generateMockMetricValue(3500, 800),
        "activeMinutes": generateMockMetricValue(120, 30),
        "heartRate": generateMockMetricValue(72, 8),
        "socialInteractions": generateMockMetricValue(5, 2),
        "moca": generateMockMetricValue(27, 1.5),
        "twoMinuteStepRank": generateMockMetricValue(82, 8),
        "chairStandRank": generateMockMetricValue(78, 10),
        "nutritionScore": generateMockMetricValue(85, 8),
        "phq9": generateMockMetricValue(3, 1.5)
      }
    },
    trends_recently: {
      name: "trends_recently",
      stateDate: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      stateDateMs: now - 7 * 24 * 60 * 60 * 1000,
      value: {
        "wellnessScore": generateMockMetricValue(75, 10, 7),
        "sleepScore": generateMockMetricValue(70, 15, 7),
        "activityScore": generateMockMetricValue(62, 12, 7),
        "bathroomVisits": generateMockMetricValue(7, 2, 7),
        "nightWakeups": generateMockMetricValue(3, 1, 7),
        "timeInBed": generateMockMetricValue(8.2, 1.5, 7),
        "stepCount": generateMockMetricValue(3200, 800, 7),
        "activeMinutes": generateMockMetricValue(110, 30, 7),
        "heartRate": generateMockMetricValue(74, 8, 7),
        "socialInteractions": generateMockMetricValue(4, 2, 7),
        "moca": generateMockMetricValue(26.5, 1.5, 7),
        "twoMinuteStepRank": generateMockMetricValue(80, 8, 7),
        "chairStandRank": generateMockMetricValue(76, 10, 7),
        "nutritionScore": generateMockMetricValue(82, 8, 7),
        "phq9": generateMockMetricValue(3.5, 1.5, 7)
      }
    },
    trends_weekly: {
      name: "trends_weekly",
      value: {
        "wellnessScore": generateMockMetricValue(76, 5),
        "sleepScore": generateMockMetricValue(71, 8),
        "activityScore": generateMockMetricValue(64, 6),
        "bathroomVisits": generateMockMetricValue(6.5, 1),
        "nightWakeups": generateMockMetricValue(2.5, 0.5),
        "timeInBed": generateMockMetricValue(8.3, 0.8),
        "stepCount": generateMockMetricValue(3350, 400),
        "activeMinutes": generateMockMetricValue(115, 15),
        "heartRate": generateMockMetricValue(73, 4),
        "socialInteractions": generateMockMetricValue(4.5, 1),
        "moca": generateMockMetricValue(26.8, 1),
        "twoMinuteStepRank": generateMockMetricValue(81, 5),
        "chairStandRank": generateMockMetricValue(77, 6),
        "nutritionScore": generateMockMetricValue(83.5, 5),
        "phq9": generateMockMetricValue(3.2, 1)
      }
    },
    trends_highlights: {
      name: "trends_highlights",
      value: {
        "wellnessScore": generateMockMetricValue(82, 8),
        "sleepScore": generateMockMetricValue(85, 10),
        "activityScore": generateMockMetricValue(78, 10),
        "bathroomVisits": generateMockMetricValue(5, 1),
        "nightWakeups": generateMockMetricValue(1, 0.5),
        "timeInBed": generateMockMetricValue(8.8, 1),
        "stepCount": generateMockMetricValue(4200, 600),
        "activeMinutes": generateMockMetricValue(150, 25),
        "heartRate": generateMockMetricValue(70, 5),
        "socialInteractions": generateMockMetricValue(7, 2),
        "moca": generateMockMetricValue(28, 1),
        "twoMinuteStepRank": generateMockMetricValue(85, 5),
        "chairStandRank": generateMockMetricValue(82, 6),
        "nutritionScore": generateMockMetricValue(88, 5),
        "phq9": generateMockMetricValue(2, 1)
      }
    }
  };
}

/**
 * Mock time series data for charts
 */
export function getMockTimeSeriesData(days: number = 30): Record<string, any[]> {
  return {
    "wellnessScore": generateMockTimeSeries(78, 10, days),
    "sleepScore": generateMockTimeSeries(72, 15, days),
    "activityScore": generateMockTimeSeries(65, 12, days),
    "bathroomVisits": generateMockTimeSeries(6, 2, days),
    "nightWakeups": generateMockTimeSeries(2, 1, days),
    "timeInBed": generateMockTimeSeries(8.5, 1.5, days),
    "stepCount": generateMockTimeSeries(3500, 800, days),
    "activeMinutes": generateMockTimeSeries(120, 30, days),
    "heartRate": generateMockTimeSeries(72, 8, days),
    "socialInteractions": generateMockTimeSeries(5, 2, days),
    "moca": generateMockTimeSeries(27, 1.5, days),
    "twoMinuteStepRank": generateMockTimeSeries(82, 8, days),
    "chairStandRank": generateMockTimeSeries(78, 10, days),
    "nutritionScore": generateMockTimeSeries(85, 8, days),
    "phq9": generateMockTimeSeries(3, 1.5, days)
  };
}

/**
 * Mock resident info
 */
export function getMockResidentInfo() {
  return {
    residentId: 'DEMO-001',
    facilityId: 'demo_facility',
    name: 'Demo Resident',
    room: '101A'
  };
}

/**
 * Mock wellness dimensions
 */
export interface WellnessDimension {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  percentage: number;
}

export function getMockWellnessDimensions(): WellnessDimension[] {
  return [
    {
      id: 'physical',
      name: 'Physical',
      icon: '💪',
      color: '#3b82f6', // Blue
      description: 'Physical health, exercise, and body wellness',
      percentage: 22
    },
    {
      id: 'mental',
      name: 'Mental',
      icon: '🧠',
      color: '#8b5cf6', // Purple
      description: 'Cognitive health, learning, and mental stimulation',
      percentage: 18
    },
    {
      id: 'emotional',
      name: 'Emotional',
      icon: '❤️',
      color: '#ec4899', // Pink
      description: 'Emotional wellbeing and mood',
      percentage: 15
    },
    {
      id: 'social',
      name: 'Social',
      icon: '👥',
      color: '#10b981', // Green
      description: 'Social connections and relationships',
      percentage: 20
    },
    {
      id: 'spiritual',
      name: 'Spiritual',
      icon: '🕊️',
      color: '#f59e0b', // Amber
      description: 'Spiritual practices and meaning',
      percentage: 12
    },
    {
      id: 'environmental',
      name: 'Environmental',
      icon: '🌿',
      color: '#14b8a6', // Teal
      description: 'Physical environment and surroundings',
      percentage: 13
    }
  ];
}

/**
 * Check if demo mode is enabled
 */
export function isDemoMode(): boolean {
  return process.env.REACT_APP_DEMO_MODE === 'true';
}

/**
 * Get demo mode indicator message
 */
export function getDemoModeMessage(): string {
  return '🎭 DEMO MODE - Displaying sample data for demonstration purposes';
}


// ---------------------------------------------------------------------------
// Demo facility configuration
//
// Demo mode promises "no API connection required", but the dashboard's config
// (metric cards, tabs, dimensions, program settings) normally comes from
// Supabase — which RLS correctly denies without an authenticated session.
// These provide an offline facility config whose metric keys match
// getMockWellnessData()/getMockTimeSeriesData() exactly.
// ---------------------------------------------------------------------------

export function getDemoCategories(): DashboardCategory[] {
  return [
    {
      id: 'demo-cat-health',
      categoryKey: 'health',
      displayName: 'Health',
      icon: 'FaHeartbeat',
      color: '#667eea',
      sortOrder: 1,
      isActive: true,
      isSystem: false,
    },
    {
      id: 'demo-cat-activity',
      categoryKey: 'activity',
      displayName: 'Activity & Sleep',
      icon: 'FaRunning',
      color: '#3b82f6',
      sortOrder: 2,
      isActive: true,
      isSystem: false,
    },
  ];
}

export function getDemoMetricConfigs(): MetricConfig[] {
  // Keys must exist in getMockWellnessData(); thresholds are tuned so the
  // mock values (78/72/65 ± jitter) land across green and yellow for a
  // representative-looking board.
  return [
    { id: 'demo-mc-1', metricKey: 'wellnessScore', displayName: 'Wellness Score', categoryId: 'demo-cat-health', units: '%', visualizationType: 'circle_gauge', greenMin: 70, greenMax: 100, yellowMin: 50, yellowMax: 69.99, isVisible: true, sortOrder: 1 },
    { id: 'demo-mc-2', metricKey: 'sleepScore', displayName: 'Sleep Quality', categoryId: 'demo-cat-activity', units: '%', visualizationType: 'horizontal_bar', greenMin: 70, greenMax: 100, yellowMin: 50, yellowMax: 69.99, isVisible: true, sortOrder: 2 },
    { id: 'demo-mc-3', metricKey: 'activityScore', displayName: 'Activity Score', categoryId: 'demo-cat-activity', units: '%', visualizationType: 'vertical_bar', greenMin: 70, greenMax: 100, yellowMin: 45, yellowMax: 69.99, isVisible: true, sortOrder: 3 },
    { id: 'demo-mc-4', metricKey: 'stepCount', displayName: 'Daily Steps', categoryId: 'demo-cat-activity', units: 'steps', visualizationType: 'line_chart', greenMin: 3000, greenMax: 20000, yellowMin: 1500, yellowMax: 2999, isVisible: true, sortOrder: 4 },
    { id: 'demo-mc-5', metricKey: 'heartRate', displayName: 'Resting Heart Rate', categoryId: 'demo-cat-health', units: 'bpm', visualizationType: 'data_card', greenMin: 55, greenMax: 80, yellowMin: 81, yellowMax: 95, isVisible: true, sortOrder: 5 },
    { id: 'demo-mc-6', metricKey: 'socialInteractions', displayName: 'Social Interactions', categoryId: 'demo-cat-activity', units: 'per day', visualizationType: 'data_card', greenMin: 3, greenMax: 50, yellowMin: 1, yellowMax: 2.99, isVisible: true, sortOrder: 6 },
    { id: 'demo-mc-7', metricKey: 'nutritionScore', displayName: 'Nutrition Score', categoryId: 'demo-cat-health', units: '%', visualizationType: 'horizontal_bar', greenMin: 70, greenMax: 100, yellowMin: 50, yellowMax: 69.99, isVisible: true, sortOrder: 7 },
  ];
}

export function getDemoDimensions(): ConfigWellnessDimension[] {
  // metricKey points at real mock metric keys so the legacy scoring path
  // (readValue of recentTrends[metricKey]) produces genuine pie percentages.
  const base = { facilityId: 'demo_facility', isActive: true, valueKey: null } as const;
  return [
    { ...base, id: 'demo-dim-physical', name: 'Physical', icon: 'FaRunning', color: '#3b82f6', description: 'Physical activity and movement', metricKey: 'activityScore', sortOrder: 1 },
    { ...base, id: 'demo-dim-sleep', name: 'Sleep', icon: 'FaBed', color: '#8b5cf6', description: 'Rest and sleep quality', metricKey: 'sleepScore', sortOrder: 2 },
    { ...base, id: 'demo-dim-social', name: 'Social', icon: 'FaUsers', color: '#ec4899', description: 'Social connection and engagement', metricKey: 'socialInteractions', sortOrder: 3 },
    { ...base, id: 'demo-dim-cognitive', name: 'Cognitive', icon: 'FaBrain', color: '#0ea5e9', description: 'Cognitive health and stimulation', metricKey: 'moca', sortOrder: 4 },
    { ...base, id: 'demo-dim-nutrition', name: 'Nutrition', icon: 'FaAppleAlt', color: '#14b8a6', description: 'Nutrition and hydration', metricKey: 'nutritionScore', sortOrder: 5 },
  ];
}

export function getDemoProgramSettings(): ProgramSettings {
  return {
    id: 'demo-ps',
    facilityId: 'demo_facility',
    programName: 'Successful Aging',
    programDescription: 'Demo mode — sample data only',
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    showProgressLeader: true,
    useEventRecommendations: false,
    useCategoryWheelColors: true,
    showBalanceNudge: true,
    recommendationMode: 'deficit',
    dimensionMetrics: [],
  };
}
